import { Container } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { AccessibleItems, AccessibleNode } from "../core/accessibility.js";
import type { Rect } from "../../common/geometry.js";
import type { RowRange } from "../core/virtualWindow.js";

/**
 * One element the mirror should be holding, as plain data.
 *
 * Flat rather than nested, with `parentKey` carrying the shape. A flat list
 * diffs in one pass and a tree does not, and the diff is the part that runs
 * ten times a second.
 */
export interface SnapshotEntry {
  /** `<control id>`, or `<control id>:<row>` for a row a control draws itself. */
  key: string;
  parentKey: string | null;
  node: AccessibleNode;
  /**
   * The control this stands for - the owning control, for a row.
   *
   * Carried so the mirror can route an activation back. A screen reader
   * activates by dispatching a click straight at the element, which reaches
   * a listener even though the element is `pointer-events: none`, and that
   * click has to become a press on something.
   */
  control: Control;
  /** The row this entry is, or null when it is the control itself. */
  itemIndex: number | null;
  /** Canvas coordinates, in UI units. */
  bounds: Rect;
  /** Behind a modal. Present in the tree, but nothing may reach it. */
  hidden: boolean;
}

export type AccessibleSnapshot = SnapshotEntry[];

/** How many rows a control may materialise before find-in-page loses out. */
export const DEFAULT_ITEM_CAP = 5000;

export interface SnapshotOptions {
  /**
   * The modal holding input, if one is. Everything outside it is marked
   * hidden rather than dropped, so opening a dialog flips one attribute per
   * element instead of tearing down and rebuilding the whole tree behind it.
   */
  modal?: Control | null;
  /**
   * `"window"` mirrors only the rows on screen, which is what keeps a
   * hundred-thousand-row list to thirty elements. `"all"` mirrors the lot, up
   * to `itemCap`, for a list short enough that Ctrl+F ought to find every row.
   */
  itemPolicy?: "window" | "all";
  itemCap?: number;
}

/**
 * Walk the control tree and describe everything reachable in it.
 *
 * Pure: it reads the tree and allocates a list, and touches no DOM. That is
 * what lets the whole of this be tested in `environment: "node"` with no GPU
 * and no jsdom - the DOM half downstream only applies patches it is handed.
 *
 * Document order is paint order, which `Container.descendants` already
 * defines. That is not a coincidence worth losing: paint order puts popups
 * and foreground children last, and last in the DOM is what a reader walks to
 * last, which is where a menu that just opened belongs.
 */
export function snapshotAccessibleTree(
  root: Container,
  options: SnapshotOptions = {},
): AccessibleSnapshot {
  const out: AccessibleSnapshot = [];
  const modal = options.modal ?? null;
  const policy = options.itemPolicy ?? "window";
  const cap = options.itemCap ?? DEFAULT_ITEM_CAP;

  const visit = (control: Control, parentKey: string | null, hidden: boolean): void => {
    // `visible` and not `effectivelyVisible`: an ancestor that was hidden
    // never recursed, so the cheap flag is the whole answer here and the walk
    // stays linear instead of climbing to the root at every node.
    if (!control.visible) return;
    // Reaching the modal itself un-hides it and everything under it, at
    // whatever depth it turns up.
    const behind = hidden && control !== modal;

    const node = control.describeAccessible();
    if (!node) {
      // Transparent: a layout panel contributes nothing and its children take
      // its place, so a reader does not walk through "group, group, group".
      if (control instanceof Container) {
        for (const child of control.controls) visit(child, parentKey, behind);
      }
      return;
    }

    const key = String(control.id);
    const bounds = control.screenBounds;
    out.push({ key, parentKey, node, control, itemIndex: null, bounds, hidden: behind });

    const items = control.accessibleItems();
    if (items) emitItems(out, control, key, bounds, items, behind, policy, cap);

    if (control instanceof Container) {
      for (const child of control.controls) visit(child, key, behind);
    }
  };

  for (const child of root.controls) visit(child, null, modal !== null);
  return out;
}

function emitItems(
  out: AccessibleSnapshot,
  control: Control,
  parentKey: string,
  parentBounds: Rect,
  items: AccessibleItems,
  hidden: boolean,
  policy: "window" | "all",
  cap: number,
): void {
  const range = rangeFor(items, policy, cap);
  for (let i = range.first; i <= range.last; i++) {
    const node = items.item(i);
    if (!node) continue;
    out.push({
      key: `${parentKey}:${i}`,
      parentKey,
      node,
      control,
      itemIndex: i,
      // Falling back to the whole control rather than to nothing: a row with
      // no box of its own still reads under a finger, just imprecisely.
      bounds: items.itemBounds?.(i) ?? parentBounds,
      hidden,
    });
  }
}

/**
 * Which rows to build.
 *
 * The cap is the point. Asking for every row of a short list is how Ctrl+F
 * finds a row that is scrolled out of sight; asking for every row of a long
 * one is a hundred thousand elements built on a frame that had sixteen
 * milliseconds, so the request is refused rather than honored slowly.
 */
export function rangeFor(items: AccessibleItems, policy: "window" | "all", cap: number): RowRange {
  if (policy === "all" && items.count <= cap) return { first: 0, last: items.count - 1 };
  return items.range;
}
